import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError, api, type DisplayOptions, type TvTextBoard, type Wallboard } from '../api/client';
import { Chip, Loading } from '../components/ui';
import { useBoardSounds } from '../lib/boardSounds';
import { QueueTile, REFRESH_MS, Stat, TextTotals, TextsTile, boardTotals, loadLimits, toneFor } from './tenant/Wallboard';
// Texts for the TV's queues (1.12.32): the numbers only, never who texted or what they wrote.
import { loadTextLimits } from '../lib/textBoard';
import { clockTime, plural } from '../lib/format';
import { Icon } from '../lib/icons';
// The ticker along the bottom: alerts, changes since the hour, and where the company's people are (1.12.13).
import { TvTicker, tickerNote, type TeamZone } from '../components/TvTicker';

type Board = Wallboard & { tenant: string; company: string; screen: string; options?: DisplayOptions; companyZone?: string; team?: TeamZone[]; texts?: TvTextBoard | null };
const NO_SOUNDS: DisplayOptions['sounds'] = { on: false, volume: 'medium', events: [] };

const passKey = (id: string) => `stitch.tv.${id}`;
function readPass(id: string): string | null {
  try {
    return localStorage.getItem(passKey(id));
  } catch {
    return null;
  }
}
/** Sound turned off on this TV itself (the Wallboard page sets whether it has sounds at all). */
const muteKey = (id: string) => `stitch.tv.${id}.muted`;
function readMuted(id: string): boolean {
  try {
    return localStorage.getItem(muteKey(id)) === '1';
  } catch {
    return false;
  }
}
function keepMuted(id: string, muted: boolean): void {
  try {
    if (muted) localStorage.setItem(muteKey(id), '1');
    else localStorage.removeItem(muteKey(id));
  } catch {
    // Private mode: it holds until the window is closed.
  }
}
function keepPass(id: string, secret: string): void {
  try {
    localStorage.setItem(passKey(id), secret);
  } catch {
    // Private mode: this window still works until it's closed.
  }
}

/**
 * A company's wallboard on a TV, in its own window: no sign-in, just this screen's read-only pass
 * (from "Open on a TV"). It keeps going after whoever set it up signs out, until it's turned off.
 */
export function TvBoard() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [secret, setSecret] = useState<string | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [problem, setProblem] = useState('');
  const [off, setOff] = useState('');
  const [now, setNow] = useState(Date.now());
  const [full, setFull] = useState(false);
  const [muted, setMuted] = useState(() => readMuted(id));
  // More queues than fit above the ticker: the tiles shrink a step at a time until they fit.
  // 1: more tiles to a row. 2: four boxes a tile, agents counted. 3: smaller boxes and totals.
  const [fit, setFit] = useState(0);
  const compact = fit >= 2;
  const windowRef = useRef<HTMLDivElement>(null);
  const queuesRef = useRef<HTMLDivElement>(null);
  // Set from the Wallboard page for this TV (the TV's own browser settings when there are none).
  const options = board?.options;
  const limits = options?.limits ?? loadLimits();
  const showAgents = options?.showAgents ?? true;
  // Texts turn amber by this TV's own browser settings (or the defaults).
  const [textLimits] = useState(loadTextLimits);

  // The link is /tv#<id>.<pass>: keep the pass on this computer, and show /tv/<id> instead.
  useEffect(() => {
    const m = /^#(tv_[A-Za-z0-9_-]{1,40})\.([A-Za-z0-9_-]{20,200})$/.exec(location.hash);
    if (m) {
      keepPass(m[1]!, m[2]!);
      setSecret(m[2]!);
      navigate(`/tv/${m[1]}`, { replace: true });
      return;
    }
    const kept = id ? readPass(id) : null;
    if (kept) setSecret(kept);
    else setOff('This screen isn’t set up on this computer. Open it again from the Wallboard page in Stitch, with “Open on a TV”.');
  }, [id, navigate]);

  useEffect(() => {
    if (!secret) return;
    let stop = false;
    const load = async () => {
      try {
        const b = await api.displayBoard(secret);
        if (stop) return;
        setBoard(b);
        setProblem('');
        document.title = `${b.company} wallboard · Stitch`;
      } catch (e) {
        if (stop) return;
        if (e instanceof ApiError && e.status === 401) {
          setOff(e.message);
          stop = true;
        } else setProblem('Can’t reach Stitch just now. Showing the last numbers, and trying again.');
      }
    };
    void load();
    const t = window.setInterval(() => !stop && void load(), REFRESH_MS);
    return () => {
      stop = true;
      window.clearInterval(t);
    };
  }, [secret]);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // Keep the TV awake, and know when full screen ends.
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const wake = async () => {
      try {
        lock = (await (navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock?.request('screen')) ?? null;
      } catch {
        lock = null;
      }
    };
    void wake();
    const onVisible = () => document.visibilityState === 'visible' && void wake();
    const onFull = () => setFull(Boolean(document.fullscreenElement));
    document.addEventListener('visibilitychange', onVisible);
    document.addEventListener('fullscreenchange', onFull);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      document.removeEventListener('fullscreenchange', onFull);
      void lock?.release().catch(() => undefined);
    };
  }, []);

  const since = board ? Math.max(0, (now - Date.parse(board.at)) / 1000) : 0;
  const stale = since > 30;
  const chosen = options?.sounds ?? NO_SOUNDS;
  // Texts waiting for each ring group or queue (1.12.33): a chime when one starts waiting, if chosen for this TV.
  const sounds = useBoardSounds(off ? null : board, { ...chosen, on: chosen.on && !muted }, limits, since, Boolean(problem) || stale, board?.texts?.teams ?? null);
  const setSound = (on: boolean) => {
    setMuted(!on);
    keepMuted(id, !on);
    if (on) void sounds.unlock();
  };
  const totals = board ? boardTotals(board.queues) : undefined;
  const textTeams = board?.texts?.teams ?? [];
  const textOf = new Map(textTeams.map((t) => [t.extension, t]));
  const textOnly = board ? textTeams.filter((t) => !board.queues.some((q) => q.extension === t.extension)) : [];
  const queueCount = (board?.queues.length ?? 0) + textOnly.length;
  // Measured before the TV paints: full tiles when they fit, compact when they don't. Looked at
  // again when the number of queues or the screen changes.
  const [size, setSize] = useState(() => `${window.innerWidth}x${window.innerHeight}`);
  useEffect(() => {
    const on = () => setSize(`${window.innerWidth}x${window.innerHeight}`);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  useLayoutEffect(() => setFit(0), [queueCount, size, showAgents, textTeams.length]);
  useLayoutEffect(() => {
    // The queue tiles must fit above the ticker; the parked calls and time of day rules below them
    // may scroll under it.
    const el = queuesRef.current;
    const win = windowRef.current;
    if (!el || !win || fit >= 3 || !queueCount) return;
    const ticker = win.querySelector('.tv-tk')?.getBoundingClientRect().top ?? window.innerHeight;
    if (el.getBoundingClientRect().bottom + win.scrollTop > ticker + 2) setFit(fit + 1);
  });

  if (off)
    return (
      <div className="wallboard tv tv-window">
        <div className="tv-off">
          <Icon name="tv" size={40} />
          <h1>Wallboard</h1>
          <p>{off}</p>
        </div>
      </div>
    );

  return (
    <div className={`wallboard tv tv-window${fit >= 1 ? ' tv-tight' : ''}${compact ? ' tv-compact' : ''}${fit >= 3 ? ' tv-dense' : ''}`} ref={windowRef}>
      <div className="content">
        <div className="tv-head">
          <strong>{board?.company ?? 'Wallboard'}</strong>
          <span className={`wb-age${stale ? ' stale' : ''}`}>{board ? (since < 2 ? 'Updated just now' : `Updated ${Math.round(since)} s ago`) : ''}</span>
          {board ? <span className="tv-ticker-note">{tickerNote(board.companyZone, now)}</span> : <span className="tv-clock">{clockTime(now)}</span>}
          {/* This TV's sounds (set on the Wallboard page): on until turned off here, remembered on this TV. */}
          {chosen.on &&
            (sounds.needsClick ? (
              <button type="button" className="tv-sound" onClick={() => void sounds.unlock()}>
                <Icon name="volume" size={18} /> Click to turn on sound
              </button>
            ) : (
              <button
                type="button"
                className={`tv-sound${muted ? ' off' : ''}`}
                aria-pressed={!muted}
                title={muted ? 'Sounds are off on this TV. Click to turn them on.' : 'Click to turn sounds off on this TV.'}
                onClick={() => setSound(muted)}
              >
                <Icon name={muted ? 'volumeOff' : 'volume'} size={18} /> {muted ? 'Sound off' : 'Sound on'}
              </button>
            ))}
          {!full && (
            <button
              type="button"
              className="icon-btn tv-full"
              onClick={() => {
                void sounds.unlock();
                void document.documentElement.requestFullscreen?.().catch(() => undefined);
              }}
              aria-label="Full screen"
              title="Full screen"
            >
              <Icon name="expand" size={20} />
            </button>
          )}
        </div>
        {problem && <p className="tv-problem">{problem}</p>}
        {!board ? (
          <Loading label="Loading the wallboard" />
        ) : (
          <>
            {((totals && board.queues.length > 1) || board.texts?.totals) && (
              <div className={`wb-strip${totals && board.queues.length > 1 && board.texts?.totals ? ' both' : ''}`}>
                {totals && board.queues.length > 1 && (
                  <div className="wb-totals" aria-label="All queues">
                    <Stat label="Waiting now" value={totals.waiting} tone={toneFor(totals.waiting, limits.waiting)} />
                    <Stat label="Agents ready" value={totals.ready} sub={`of ${totals.loggedIn} signed in`} />
                    <Stat label="On a call" value={totals.busy} />
                    <Stat label="Answered today" value={totals.answered} />
                    <Stat label="Hung up waiting" value={totals.missed} />
                  </div>
                )}
                {board.texts?.totals && <TextTotals n={board.texts.totals} limits={textLimits} since={since} />}
              </div>
            )}
            <div className="wb-queues" ref={queuesRef}>
              {board.queues.map((q) => (
                <QueueTile
                  key={q.extension}
                  q={q}
                  limits={limits}
                  since={since}
                  showAgents={showAgents}
                  compact={compact}
                  {...(textOf.has(q.extension) ? { texts: textOf.get(q.extension)!, textLimits } : {})}
                />
              ))}
              {textOnly.map((t) => (
                <TextsTile key={t.extension} t={t} limits={textLimits} since={since} showAgents={showAgents} compact={compact} />
              ))}
            </div>
            {(board.parked.length > 0 || board.rules.length > 0) && (
              <div className="wb-side">
                {board.parked.length > 0 && (
                  <div className="wb-panel">
                    <span className="wb-panel-title">
                      <Icon name="park" size={16} /> Parked calls
                    </span>
                    {board.parked.map((p) => (
                      <span key={p.extension} className="wb-line">
                        <span>{p.name}</span>
                        <Chip small tone="warn">
                          {plural(p.calls, 'call')}
                        </Chip>
                      </span>
                    ))}
                  </div>
                )}
                {board.rules.length > 0 && (
                  <div className="wb-panel">
                    <span className="wb-panel-title">
                      <Icon name="clock" size={16} /> Time of day rules
                    </span>
                    {board.rules.map((r) => (
                      <span key={r.extension} className="wb-line">
                        <span>{r.name}</span>
                        <Chip small tone={r.on ? 'warn' : 'ok'}>
                          {r.on ? 'On' : 'Off'}
                        </Chip>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {board && <TvTicker id={id} company={board.company} companyZone={board.companyZone} queues={board.queues} team={board.team} now={now} />}
    </div>
  );
}
